import React, { FC, MouseEvent, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { OfferedShipment, useDeclineBid, usePostWaterfallReoffer } from 'apps/fleet-planner/types'
import { getOfferStatus, getOfferStatusMessage } from 'apps/fleet-planner/utils/offerHelpers'
import { LaneArrowIcon } from 'transfix-icons'
import { Button, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { formatCityState } from 'utils/formatCityState'
import formatCurrency from 'utils/formatCurrency'
import { OfferAppointmentDateTime } from './OfferAppointmentDateTime'

export const useOfferRowStyles = makeStyles(() => ({
  coolGray5: {
    color: brandColors.coolGray5,
  },
  coolGray8: {
    color: brandColors.coolGray8,
  },
  skyBlue6: {
    color: brandColors.skyBlue6,
  },
  divider: {
    border: `1px solid #F1F3F5`,
  },
}))

interface IOfferRowProps {
  shipmentOffer: OfferedShipment
  isOfferUnavailable?: boolean
  variant: 'home' | 'offer'
  onDeclineHandler: (response: string) => void
}

export const OfferRow: FC<IOfferRowProps> = ({
  isOfferUnavailable,
  onDeclineHandler,
  shipmentOffer,
  variant,
}) => {
  const classes = useOfferRowStyles()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isfmsDeclineBidEnabled } = getFlag('fmsDeclineBid')
  const { trackEvent } = useFMSAnalytics()
  const {
    deliveryCity,
    deliveryState,
    externalId = 0,
    pickupCity,
    pickupState,
    stops = [],
    offeredRate,
    offeredState,
  } = shipmentOffer || {}
  const {
    error,
    loading: isPostWaterfallReofferLoading,
    mutate: postWaterfallReoffer,
  } = usePostWaterfallReoffer({ id: externalId })
  const [declineLoading, setDeclineLoading] = useState<boolean>(false)
  const { mutate: declineOffer } = useDeclineBid({ id: externalId })
  const [firstStop] = stops
  const lastStop = stops[stops.length - 1]
  const history = useHistory()
  const showDecline = offeredState !== 'dedicated_rate_offered_to_more_carriers'
  const bookHandler = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (offeredState === 'dedicated_rate_offered_to_more_carriers') {
      trackEvent(
        'Offers',
        variant === 'home' ? 'CLICKED_BOOK_NOW_IN_HOME_PAGE' : 'CLICKED_BOOK_NOW_IN_OFFER_PAGE',
        { offeredState }
      )
      try {
        await postWaterfallReoffer()
        history.push(`/book/${externalId}/rate-confirmation`)
      } catch (e) {
        // no-op
        // error is captured in usePostWaterfallReoffer
      }
    } else {
      history.push(`/book/${externalId}/rate-confirmation`)
    }
  }

  const declineHandler = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setDeclineLoading(true)
    try {
      const { response = '' } = await declineOffer()
      trackEvent(
        'Offers',
        variant === 'home' ? 'CLICKED_DECLINE_IN_HOME_PAGE' : 'CLICKED_DECLINE_IN_OFFER_PAGE',
        { offeredState }
      )
      setDeclineLoading(false)
      onDeclineHandler(response)
    } catch (e) {
      setDeclineLoading(false)
      onDeclineHandler('') // surface error message
    }
  }

  return (
    <div>
      <Typography variant='overline' className={classes.coolGray5}>
        #TFX-{externalId}
      </Typography>
      <Box display='flex'>
        <div>
          <Box display='flex' alignItems='center'>
            <Box pr={1}>
              <Typography className={isOfferUnavailable ? classes.coolGray5 : ''} variant='body1'>
                {formatCityState(pickupCity, pickupState)}
              </Typography>
            </Box>
            <LaneArrowIcon color={isOfferUnavailable ? 'coolGray5' : 'black'} size='large' />
            <Box pl={1}>
              <Typography className={isOfferUnavailable ? classes.coolGray5 : ''} variant='body1'>
                {formatCityState(deliveryCity, deliveryState)}
              </Typography>
            </Box>
          </Box>
          <Box display='flex'>
            <Typography component='span' variant='body2' className={classes.coolGray5}>
              <OfferAppointmentDateTime stop={firstStop} /> -{' '}
              <OfferAppointmentDateTime stop={lastStop} />
            </Typography>
          </Box>
        </div>
        <Box marginLeft='auto'>
          <Box display='flex' flexDirection='row-reverse' pt={1} pb={0.7}>
            <Typography
              className={isOfferUnavailable ? classes.coolGray5 : classes.coolGray8}
              variant='subtitle1'>
              {formatCurrency(offeredRate)}
            </Typography>
          </Box>
          <Typography className={classes.coolGray5} variant='subtitle2'>
            {isOfferUnavailable ? 'Load Unavailable' : `${getOfferStatus(shipmentOffer)}`}
          </Typography>
        </Box>
      </Box>
      {!isOfferUnavailable && (
        <div>
          <Box pt={1} pb={1}>
            <div className={classes.divider} />
          </Box>
          <Box display='flex' alignItems='center'>
            {error ? (
              <InlineMessage message={error?.message} type={INLINE_MESSAGE_VARIANTS.ALERT} />
            ) : (
              <Typography className={classes.skyBlue6} variant='body2'>
                {getOfferStatusMessage(shipmentOffer)}
              </Typography>
            )}
            <Box marginLeft='auto'>
              {isfmsDeclineBidEnabled && showDecline && (
                <Button
                  color='tertiary'
                  size='large'
                  onClick={declineHandler}
                  disabled={declineLoading}>
                  Decline
                </Button>
              )}
              <Button disabled={isPostWaterfallReofferLoading} onClick={bookHandler} size='large'>
                Accept Offer
              </Button>
            </Box>
          </Box>
        </div>
      )}
    </div>
  )
}
